import { useTranslation } from 'react-i18next';

import { useIncidentCommand } from '@/features/shared/useIncidentCommand';

import { NgButton, PanelInfoWithIcon } from '@/ui';
import type { CallerConnectedStatus, LiveVideo } from '@/signalr';
import { IncidentCommandFeedback } from '@/features/shared/IncidentCommandFeedback';
import { MediasoupVideoBox } from './MediasoupVideoBox';
import { VideoRecordingsBox } from './VideoRecordingsBox';

export type VideoBoxProps = {
  incidentId: string;
  incidentIsClosed: boolean;
  incidentIsReadOnly: boolean;
  incidentHasPhoneNumber: boolean;
  hasAppleEedVideoCapability: boolean;
  allowAppleEedVideoActions: boolean;
  appleEedVideoCallId?: string | null;
  liveVideos: readonly LiveVideo[];
  activeLiveVideoId: string | null;
  videoExpanded: boolean;
  onVideoExpandedChanged: (expanded: boolean) => void;
  callerConnectedStatus: CallerConnectedStatus;
};

export function VideoBox({
  incidentId,
  incidentIsClosed,
  incidentIsReadOnly,
  incidentHasPhoneNumber,
  hasAppleEedVideoCapability,
  allowAppleEedVideoActions,
  appleEedVideoCallId,
  liveVideos,
  activeLiveVideoId,
  videoExpanded,
  onVideoExpandedChanged,
}: VideoBoxProps) {
  const { t } = useTranslation();

  const allowStartingVideo = incidentHasPhoneNumber;
  const activeLiveVideo = liveVideos.find((video) => video.id === activeLiveVideoId) ?? null;
  const videoCommand = useIncidentCommand(
    incidentId,
    'videoChat',
    activeLiveVideo?.id ?? null,
    !incidentIsClosed && !incidentIsReadOnly && incidentHasPhoneNumber && activeLiveVideo === null,
  );

  if (incidentIsReadOnly && liveVideos.length > 0) {
    return (
      <div className="video-box p-3 justify-content-center">
        <VideoRecordingsBox incidentId={incidentId} liveVideos={liveVideos} />
      </div>
    );
  }

  if (incidentIsClosed === false && !incidentIsReadOnly) {
    if (activeLiveVideo !== null) {
      return (
        <MediasoupVideoBox
          key={activeLiveVideo.id}
          incidentId={incidentId}
          liveVideo={activeLiveVideo}
          allowAppleEedVideoActions={allowAppleEedVideoActions}
          appleEedVideoCallId={appleEedVideoCallId}
          videoExpanded={videoExpanded}
          onVideoExpandedChanged={onVideoExpandedChanged}
        />
      );
    }

    if (videoCommand.status !== 'idle') {
      return (
        <div
          className="video-box justify-content-center"
          data-testid={
            videoCommand.status === 'pending' ? 'incident-video-starting-panel' : 'incident-video-start-failed-panel'
          }
        >
          <IncidentCommandFeedback
            action="videoChat"
            command={videoCommand}
            allowRetry={allowStartingVideo}
            progressTestID="incident-video-starting-status"
          />
        </div>
      );
    }

    return (
      <div className="video-box" data-testid="incident-video-inactive-panel">
        <VideoRecordingsBox incidentId={incidentId} liveVideos={liveVideos} allowDivider />

        {hasAppleEedVideoCapability ? (
          <PanelInfoWithIcon icon="fal fa-video" text={t('Incident_Video_AppleEedVideoCapable')}>
            {allowStartingVideo && (
              <NgButton
                text={t('Incident_Button_InitiateVideo')}
                icon="fal fa-video-plus"
                size="small"
                testID="incident-start-video-button"
                onClick={async () => {
                  await videoCommand.start();
                }}
                enableMultipleClickPrevention
                showProgress
              />
            )}
          </PanelInfoWithIcon>
        ) : (
          <PanelInfoWithIcon icon="fal fa-video-slash" text={t('Incident_Video_VideoInactive')}>
            {allowStartingVideo && (
              <NgButton
                text={t('Incident_Button_InitiateVideo')}
                icon="fal fa-video-plus"
                size="small"
                testID="incident-start-video-button"
                onClick={async () => {
                  await videoCommand.start();
                }}
                enableMultipleClickPrevention
                showProgress
              />
            )}
          </PanelInfoWithIcon>
        )}
      </div>
    );
  }

  return null;
}
